import {
    TimeEntry,
    TimeEntryType,
    Company,
    MealsMode,
    Position,
    Department,
    Venue,
    BreakMode,
} from "./model";
import { parseTimes, parseDateString, getWeekNumber } from "./utils";
import { Hours, Meals, add, max, Minutes } from "@pentacode/openapi";
import { PentacodeAPIModels } from "./rest/api";
import { AutoBreak, getAutomaticBreak } from "./breaks";
import { overlap } from "./time/interval";
import { hour } from "./time/consts";
import { addTimeResults, getTotalResults, TimeResult } from "./time/time-result";
import { DailyResultsDTO } from "./api/rpc";

export * from "./time/consts";
export * from "./time/interval";
export * from "./time/date-range";
export * from "./time/time-result";
export { BonusResult } from "./time/bonus-result";
export * from "./time/balances";
export * from "./time/nominals";
export * from "./time/daily-results";

/**
 * Mutates the given date by applying rounding to the minutes.
 *
 * If `rounding` is positive, the date is rounded up, if negative, it is rounded down.
 *
 * The date is also rounded down to the full minute.
 *
 * @param date The date to apply rounding to
 * @param rounding The rounding to apply in Minutes
 */
export function applyRounding(date: Date, rounding: Minutes) {
    if (rounding) {
        const minutes = date.getMinutes();
        // Minutes subtracted to round down
        const deltaRoundDown = minutes % rounding;
        // If rounding is positive, rounding is added to round up
        const deltaRoundUp = Math.max(0, rounding);

        // apply roundings
        date.setMinutes(minutes - deltaRoundDown + deltaRoundUp);
    }

    // Round to full minute
    date.setSeconds(0);
    date.setMilliseconds(0);
}

/**
 * Corrects to `plannedDate` if `date` was logged before `plannedDate` but rounded up beyond it
 * or if it was logged after plannedDate, but rounded down to earlier than it
 * @param date The date to be corrected
 * @param loggedTime The date `date` was logged
 * @param plannedDate The date that `date` should be corrected to
 * @returns Copy of `plannedDate` if correction is neccessary, otherwise `date`
 */
export function correctRoundedDate(date: Date, loggedTime: Date, plannedDate: Date | null) {
    if (!plannedDate) return date;

    if ((loggedTime <= plannedDate && date > plannedDate) || (loggedTime >= plannedDate && date < plannedDate)) {
        return new Date(plannedDate);
    }

    return date;
}

export function getStartFinal(
    timeEntry: TimeEntry,
    loggedTime: Date,
    useActualTimeAtShiftStart: boolean,
    shiftStartRounding: Minutes
) {
    // If logged time is earlier than planned and useActualTimeAtShiftStart setting
    // is not set, use planned time instead
    let startFinal =
        timeEntry.startPlanned && loggedTime < timeEntry.startPlanned && !useActualTimeAtShiftStart
            ? new Date(timeEntry.startPlanned)
            : new Date(loggedTime);

    applyRounding(startFinal, shiftStartRounding);

    startFinal = correctRoundedDate(startFinal, loggedTime, timeEntry.startPlanned);

    return startFinal;
}

export function getEndFinal(
    timeEntry: TimeEntry,
    loggedTime: Date,
    useActualTimeAtShiftEnd: boolean,
    shiftEndRounding: Minutes
) {
    // If logged time is later than planned and useActualTimeAtShiftEnd setting
    // is not set, use planned time instead
    let endFinal =
        timeEntry.endPlanned && loggedTime > timeEntry.endPlanned && !useActualTimeAtShiftEnd
            ? new Date(timeEntry.endPlanned)
            : new Date(loggedTime);

    applyRounding(endFinal, shiftEndRounding);

    endFinal = correctRoundedDate(endFinal, loggedTime, timeEntry.endPlanned);

    // Make sure end time is not earlier than start time
    if (timeEntry.startFinal && endFinal < timeEntry.startFinal) {
        endFinal = timeEntry.startFinal;
    }

    return endFinal;
}

export function applyAutoMeals(
    company: Company,
    timeEntry: TimeEntry,
    { breakfast, lunch, dinner }: { breakfast: boolean; lunch: boolean; dinner: boolean }
) {
    const timeSettings = company.getTimeSettings({ timeEntry });
    if (
        timeSettings.mealsMode !== MealsMode.Auto ||
        !timeEntry.final ||
        timeEntry.durationFinal < timeSettings.mealsMinDuration
    ) {
        return;
    }

    const breakfastInterval =
        timeSettings.mealEnabledBreakfast &&
        timeSettings.mealStartBreakfast &&
        timeSettings.mealEndBreakfast &&
        (parseTimes(timeEntry.date, timeSettings.mealStartBreakfast, timeSettings.mealEndBreakfast) as [Date, Date]);

    const lunchInterval =
        timeSettings.mealEnabledLunch &&
        timeSettings.mealStartLunch &&
        timeSettings.mealEndLunch &&
        (parseTimes(timeEntry.date, timeSettings.mealStartLunch, timeSettings.mealEndLunch) as [Date, Date]);

    const dinnerInterval =
        timeSettings.mealEnabledDinner &&
        timeSettings.mealStartDinner &&
        timeSettings.mealEndDinner &&
        (parseTimes(timeEntry.date, timeSettings.mealStartDinner, timeSettings.mealEndDinner) as [Date, Date]);

    if (breakfastInterval && breakfast && overlap(timeEntry.final, [breakfastInterval])) {
        timeEntry.mealsBreakfast = 1 as Meals;
    }

    if (dinnerInterval && dinner && overlap(timeEntry.final, [dinnerInterval])) {
        timeEntry.mealsDinner = 1 as Meals;
    }

    if (lunchInterval && lunch && overlap(timeEntry.final, [lunchInterval])) {
        timeEntry.mealsLunch = 1 as Meals;
    }
}

export function applyAutoBreaks(entry: TimeEntry, breakMode: BreakMode, autoBreaks: AutoBreak[]) {
    const breakAuto = [BreakMode.Planned, BreakMode.PlannedPlusManual, BreakMode.PlannedOrManual].includes(breakMode)
        ? entry.breakPlanned || (0 as Hours)
        : (getAutomaticBreak(entry.durationFinal, { autoBreaks, breakMode }) as Hours);
    const prevBreak = entry.break || (0 as Hours);
    const breakLogged = entry.breakLogged || (0 as Hours);

    if (entry.break === null || breakAuto !== entry.breakAuto) {
        const prevBreakAuto = entry.breakAuto ?? breakAuto;
        entry.breakAuto = breakAuto;

        const brk = [BreakMode.AutoOrManual, BreakMode.PlannedOrManual].includes(breakMode)
            ? max(breakAuto, breakLogged)
            : add(breakAuto, breakLogged);

        const prevBreakAutoWithManual = [BreakMode.AutoOrManual, BreakMode.PlannedOrManual].includes(breakMode)
            ? max(prevBreakAuto, breakLogged)
            : add(prevBreakAuto, breakLogged);

        // If break is null or hasn't been edited after the fact, update value
        if (entry.break === null || prevBreak - prevBreakAutoWithManual * 60 < 1) {
            entry.break = brk;
        }
    }
}

export type TimeResultsBreakdown = {
    items: {
        type: TimeEntryType;
        result: TimeResult;
        workArea: {
            position: Position;
            department: Department;
            venue: Venue;
        } | null;
        comment?: string;
    }[];
    total: TimeResult;
};

export function getResultBreakDown(company: Company, timeEntries: TimeEntry[]) {
    timeEntries = timeEntries.filter((e) => e.type !== TimeEntryType.VacationAdjustment && Boolean(e.result));

    const breakdown: TimeResultsBreakdown = {
        items: [],
        total: getTotalResults(timeEntries.filter((e) => Boolean(e.result)).map((entry) => entry.result!)),
    };

    const items = new Map<string, TimeResultsBreakdown["items"][number]>();

    for (const { type, positionId, result, comment } of timeEntries) {
        if (!result) {
            continue;
        }
        const key = [TimeEntryType.HourAdjustment, TimeEntryType.VacationAdjustment].includes(type)
            ? `${type}_${positionId}_${comment}`
            : `${type}_${positionId}`;
        const existing = items.get(key);

        if (existing) {
            existing.result = addTimeResults(existing.result, result);
        } else {
            const workArea = (positionId && company.getPosition(positionId)) || null;
            items.set(key, {
                type,
                result,
                workArea,
                comment: [TimeEntryType.HourAdjustment, TimeEntryType.VacationAdjustment].includes(type)
                    ? comment
                    : undefined,
            });
        }
    }

    breakdown.items = [...items.values()];

    return breakdown;
}

export function getAvailableActions(
    timeEntry: PentacodeAPIModels["TimeEntry"],
    settings: PentacodeAPIModels["TimeLogSettings"]
): PentacodeAPIModels["TimeLogAction"][] {
    const startString = timeEntry.startFinal || timeEntry.startPlanned || new Date();

    const startTime = new Date(startString).getTime();

    const canStart = startTime - settings.allowEarlyStart * hour < Date.now();
    const isUnlocked = Date.now() < startTime + settings.lockAfter * hour;
    const shiftReady = canStart && isUnlocked;

    // Can't do anything if shift is not ready
    if (!shiftReady) return [];

    // If paused, can end the break or the shift
    if (timeEntry.status === "paused") {
        return ["endBreak", "endShift"];
    }

    // If ongoing, can end the shift or start a break if allowed
    if (timeEntry.status === "ongoing") {
        if (settings.manualBreaksAllowed) {
            return ["startBreak", "endShift"];
        }
        return ["endShift"];
    }

    // If scheduled, can start the shift
    if (timeEntry.status === "scheduled") {
        return ["startShift"];
    }

    // Default (e.g completed), can't do anything
    return [];
}

export function getCommonWorkDays(dailyResults: DailyResultsDTO[]) {
    const daysPerWeekDay = [0, 0, 0, 0, 0, 0, 0];
    const daysPerWeek = new Map<string, number>();
    for (const dailyResult of dailyResults) {
        const isWorkOrAbsenceDay =
            dailyResult.results.work.base.duration > 0 || dailyResult.results.absences.base.duration > 0;
        const weekDay = new Date(dailyResult.date).getDay();
        if (isWorkOrAbsenceDay) {
            daysPerWeekDay[weekDay]++;
            const week = `${parseDateString(dailyResult.date)!.getFullYear()},${getWeekNumber(dailyResult.date)}`;
            daysPerWeek.set(week, (daysPerWeek.get(week) || 0) + 1);
        }
    }
    const daysPerWeekArr = Array.from(daysPerWeek.values());
    const totaldays = daysPerWeekArr.reduce((a, b) => a + b, 0);
    const weeksWorked = daysPerWeekArr.filter((val) => val > 0).length;
    const averageDaysPerWeek = Math.round(totaldays / weeksWorked);
    const commonWorkDays = daysPerWeekDay
        .map((count, i) => [count, i])
        .sort(([a], [b]) => b - a)
        .slice(0, averageDaysPerWeek)
        .map(([, i]) => i);

    return commonWorkDays;
}
